import { useRef, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { fl } from "@/lib/finI18n";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Download, Upload, AlertTriangle } from "lucide-react";

const ids = (npwp, nik) => [npwp && `NPWP ${npwp}`, nik && `NIK ${nik}`].filter(Boolean).join(" · ") || "—";

function Stat({ k, v, lang, warn }) {
  return (
    <div className={`rounded-md border px-3 py-2 ${warn && v ? "border-amber-300 bg-amber-50 dark:bg-amber-950/30" : "border-border"}`} data-testid={`ti-stat-${k}`}>
      <div className="text-[11px] text-muted-foreground">{fl(`ti${k}`, lang)}</div>
      <div className="num text-lg font-semibold">{v}</div>
    </div>
  );
}

function ResultDialog({ res, lang, onClose }) {
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="ti-result">
        <DialogHeader><DialogTitle>{fl("tiResult", lang)}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
          <Stat k="Filled" v={res.filled} lang={lang} />
          <Stat k="Created" v={res.created.length} lang={lang} />
          <Stat k="Changed" v={res.changed.length} lang={lang} warn />
          <Stat k="Unchanged" v={res.unchanged} lang={lang} />
          <Stat k="Errors" v={res.errors.length} lang={lang} warn />
        </div>
        <p className="text-sm" data-testid="ti-remaining">{fl("tiRemaining", lang)}: <b className="num">{res.remaining}</b></p>
        {res.changed.length > 0 && (
          <div className="space-y-1.5" data-testid="ti-changed">
            <p className="text-xs text-amber-800 dark:text-amber-300 flex gap-1.5"><AlertTriangle className="h-4 w-4 shrink-0" />{fl("tiChangedHint", lang)}</p>
            <Table>
              <TableHeader><TableRow><TableHead>{fl("ledger", lang)}</TableHead><TableHead>{fl("tiOld", lang)}</TableHead><TableHead>{fl("tiNew", lang)}</TableHead></TableRow></TableHeader>
              <TableBody>
                {res.changed.map((c, i) => (
                  <TableRow key={c.ledger_id} data-testid={`ti-changed-row-${i}`}>
                    <TableCell className="text-sm font-medium">{c.name}</TableCell>
                    <TableCell className="text-xs num text-muted-foreground line-through">{ids(c.old_npwp, c.old_nik)}</TableCell>
                    <TableCell className="text-xs num font-semibold">{ids(c.npwp, c.nik)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
        {res.created.length > 0 && (
          <div className="space-y-1" data-testid="ti-created">
            <p className="text-xs font-semibold">{fl("tiCreated", lang)}</p>
            <p className="text-xs text-muted-foreground">{res.created.join(", ")}</p>
          </div>
        )}
        {res.errors.length > 0 && (
          <div className="space-y-1.5" data-testid="ti-errors">
            <p className="text-xs font-semibold text-rose-600">{fl("tiErrors", lang)}</p>
            <Table>
              <TableHeader><TableRow><TableHead>{fl("tiRow", lang)}</TableHead><TableHead>{fl("ledger", lang)}</TableHead><TableHead>{fl("desc", lang)}</TableHead></TableRow></TableHeader>
              <TableBody>
                {res.errors.map((e, i) => (
                  <TableRow key={`${e.row}-${i}`} data-testid={`ti-error-row-${i}`}>
                    <TableCell className="text-xs num">{e.row}</TableCell><TableCell className="text-xs">{e.name || "—"}</TableCell>
                    <TableCell className="text-xs text-rose-600">{fl(`tiErr_${e.error}`, lang)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
        {res.empty > 0 && <p className="text-[11px] text-muted-foreground">{fl("tiEmpty", lang)}: <span className="num">{res.empty}</span></p>}
        {res.warnings?.length > 0 && (
          <div className="space-y-1.5" data-testid="ti-warnings">
            <p className="text-xs font-semibold text-amber-700 dark:text-amber-300">{fl("tiWarnings", lang)}</p>
            <Table>
              <TableHeader><TableRow><TableHead>{fl("tiRow", lang)}</TableHead><TableHead>{fl("ledger", lang)}</TableHead><TableHead>{fl("desc", lang)}</TableHead></TableRow></TableHeader>
              <TableBody>
                {res.warnings.map((w, i) => (
                  <TableRow key={`${w.row}-${i}`} data-testid={`ti-warning-row-${i}`}>
                    <TableCell className="text-xs num">{w.row}</TableCell><TableCell className="text-xs">{w.name}</TableCell>
                    <TableCell className="text-xs text-amber-700 dark:text-amber-300">{w.issues.map((c) => fl(`tiIss_${c}`, lang)).join(" · ")}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
        <DialogFooter><Button onClick={onClose} data-testid="ti-result-close">OK</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Download a template pre-filled with incomplete counterparties, then upload it back to fill every NPWP/NIK at once.
export function TaxIdImport({ clientId, lang, onDone }) {
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const input = useRef(null);
  const download = async () => {
    try {
      const r = await api.get(`/tax-ids/template?client_id=${clientId}&lang=${lang}`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = "npwp_nik_lawan_transaksi.xlsx"; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("client_id", clientId);
    fd.append("lang", lang);
    fd.append("file", file);
    try {
      const { data } = await api.post("/tax-ids/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setRes(data);
      toast.success(fl("tiDone", lang));
      if (onDone) onDone();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(d instanceof Blob ? fl("tiErrors", lang) : apiErr(d));
    } finally { setBusy(false); if (input.current) input.current.value = ""; }
  };
  return (
    <>
      <Button variant="outline" size="sm" className="h-9" onClick={download} data-testid="ti-template-btn"><Download className="h-4 w-4 mr-1" />{fl("tiTemplate", lang)}</Button>
      <Button variant="outline" size="sm" className="h-9" onClick={() => input.current?.click()} disabled={busy} data-testid="ti-upload-btn">
        <Upload className="h-4 w-4 mr-1" />{busy ? fl("tiUploading", lang) : fl("tiUpload", lang)}
      </Button>
      <input ref={input} type="file" accept=".xlsx,.csv" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid="ti-upload-input" />
      {res && <ResultDialog res={res} lang={lang} onClose={() => setRes(null)} />}
    </>
  );
}
